import React, { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AuthenticationContext } from '../../components/AuthenticationContext';
import { Player } from '../../api';
import Login from '../Login';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { GQL_GET_CAMERA_DETAIL } from '../../graphql/queries/cameras';
import { GQL_CAMERA_RESET, GQL_CAMERA_TEST } from '../../graphql/mutations/cameras';
import { DateTime } from 'luxon';
import GoToCamera from './GoToCamera';
import { ROLE_CAMERA_EDIT, ROLE_NO_STATS, ROLE_PLAYER_EDIT } from '../../api/account';
import RTSPPush from './RTSPPush';
import HotLinkUpdate from './HotlinkUpdate';

export default function CameraDetail() {
  const history = useHistory();
  const { authorized, removeToken } = useContext(AuthenticationContext);
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any | null>(null);
  const [reseting, setReseting] = useState(false);
  const [goToCamera, setGoToCamera] = useState(false);
  const [error, setError] = useState('');
  const [showStatsLink, setShowStatsLink] = useState(false);
  const [showCameraEditLink, setShowCameraEditLink] = useState(false);
  const [showPlayerEditLink, setShowPlayerEditLink] = useState(false);
  const [hotlinkPlayer, setHotlinkPlayer] = useState<any>(null);
  const { data } = useQuery(GQL_GET_CAMERA_DETAIL, {
    onError(error) {
      if (error.graphQLErrors.findIndex((err) => err?.extensions?.code == 'UNAUTHENTICATED') != -1) {
        removeToken();
      } else {
        setError(error.message);
        console.error(error);
      }
      setLoading(false);
    },
    onCompleted(data) {
      setLoading(false);
      setShowStatsLink(data.account.roles.indexOf(ROLE_NO_STATS) == -1);
      setShowCameraEditLink(data.account.roles.indexOf(ROLE_CAMERA_EDIT) != -1);
      setShowPlayerEditLink(data.account.roles.indexOf(ROLE_PLAYER_EDIT) != -1);
    },
    variables: {
      cameraId: id,
    },
    skip: !authorized,
  });
  console.log(data);

  const [test] = useMutation(GQL_CAMERA_TEST);
  const [reset] = useMutation(GQL_CAMERA_RESET);

  const onTest = function () {
    setTesting(true);
    const variables = {
      cameraId: id,
    };
    test({ variables }).then((data: any) => {
      setTesting(false);
      setTestResult(data.data.cameraTest);
      console.log(data);
    });
  };
  const handleTestClose = function () {
    setTestResult(null);
  };

  const onReset = function () {
    setReseting(true);
    const variables = {
      cameraId: id,
    };
    reset({ variables }).then((data: any) => {
      console.log(data);
      setReseting(false);
    });
  };

  if (!authorized) return <Login />;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid container item spacing={3} alignItems="center">
                  <Grid item flexGrow={1}>
                    <h4
                      style={{ margin: 0, cursor: 'pointer' }}
                      onClick={() => {
                        history.push(`/`);
                      }}
                    >
                      {data?.camera?.client?.name}
                    </h4>
                    <h1 style={{ margin: '0 0 20px' }}>{data?.camera?.name}</h1>
                  </Grid>
                  <Grid item>
                    {showCameraEditLink && (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => history.push(`/camera/${data?.camera?.id}/settings`)}
                      >
                        Settings
                      </Button>
                    )}
                  </Grid>
                  <Grid item>
                    {showStatsLink && (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => history.push(`/stats/camera/${data?.camera?.id}`)}
                      >
                        Stats
                      </Button>
                    )}
                  </Grid>
                </Grid>
                {data?.camera?.status?.time && (
                  <Grid item container spacing={2}>
                    <Grid item xs>
                      <b>Last Test:</b>{' '}
                      {DateTime.fromMillis(data.camera.status.time).toLocaleString(DateTime.DATETIME_SHORT)}
                    </Grid>
                    <Grid item xs>
                      {data?.camera?.status?.lastChange && (
                        <>
                          <b>Last Status Change:</b>{' '}
                          {DateTime.fromMillis(data.camera.status.lastChange).toLocaleString(DateTime.DATETIME_SHORT)}
                        </>
                      )}
                    </Grid>
                    <Grid item xs="auto">
                      <Button variant="contained" color="secondary" onClick={onTest} disabled={testing || loading}>
                        Test&nbsp; {testing && <CircularProgress size={20} thickness={7} />}
                      </Button>
                    </Grid>
                  </Grid>
                )}
                {data?.camera?.status?.http && (
                  <Grid item container spacing={2}>
                    <Grid item>
                      <b>HTTP:</b>
                    </Grid>
                    <Grid item>
                      {data.camera?.status?.http?.online === true && (
                        <Chip label="online" size="small" color="primary" sx={{ margin: '0 5px' }} />
                      )}
                      {data.camera?.status?.http?.online === false && (
                        <Chip label="offline" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                      {data.camera?.status?.http?.replied === true && (
                        <Chip label="reply" size="small" color="primary" sx={{ margin: '0 5px' }} />
                      )}
                      {data.camera?.status?.http?.replied === false && (
                        <Chip label="not replying" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                      {data.camera?.status?.http?.auth === true && (
                        <Chip label="authenticate" size="small" color="primary" sx={{ margin: '0 5px' }} />
                      )}
                      {data.camera?.status?.http?.auth === false && (
                        <Chip label="bad credentials" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                      {data.camera?.status?.http?.timeout == true && (
                        <Chip label="timeout" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                      {data.camera?.status?.http?.error == true && (
                        <Chip
                          label={`error: ${data.camera.status.http.errorMessage}`}
                          size="small"
                          color="secondary"
                          sx={{ margin: '0 5px' }}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}

                {data?.camera?.status?.rtsp && (
                  <Grid item container spacing={2}>
                    <Grid item>
                      <b>RTSP:</b>
                    </Grid>
                    <Grid item>
                      {data?.camera?.status?.rtsp?.online === true && (
                        <Chip label="online" size="small" color="primary" sx={{ margin: '0 5px' }} />
                      )}
                      {data?.camera?.status?.rtsp?.online === false && (
                        <Chip label="offline" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                      {data?.camera?.status?.rtsp?.connected === true && (
                        <Chip label="connect" size="small" color="primary" sx={{ margin: '0 5px' }} />
                      )}
                      {data?.camera?.status?.rtsp?.connected === false && (
                        <Chip label="not connecting" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                      {data?.camera?.status?.rtsp?.requested === true && (
                        <Chip label="listening" size="small" color="primary" sx={{ margin: '0 5px' }} />
                      )}
                      {data?.camera?.status?.rtsp?.requested === false && (
                        <Chip label="not listening" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                      {data?.camera?.status?.rtsp?.error == true && (
                        <Chip label="error" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                      {data?.camera?.status?.rtsp?.responseCode && (
                        <Chip
                          label={`Response: ${data?.camera?.status.rtsp.responseCode}`}
                          size="small"
                          color={data?.camera?.status.rtsp.responseCode == 200 ? 'primary' : 'secondary'}
                          sx={{ margin: '0 5px' }}
                        />
                      )}
                      {data?.camera?.status?.rtsp?.timeout == true && (
                        <Chip label="timeout" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                      )}
                    </Grid>
                  </Grid>
                )}
                <Grid container item spacing={2}>
                  <Grid item xs></Grid>
                  <Grid item xs="auto">
                    <Button variant="contained" color="secondary" onClick={onReset} disabled={reseting || loading}>
                      Reset Stream&nbsp; {reseting && <CircularProgress size={20} thickness={7} />}
                    </Button>
                  </Grid>
                  <Grid item xs="auto">
                    <Button variant="contained" color="secondary" onClick={() => setGoToCamera(true)}>
                      Go To Camera
                    </Button>
                  </Grid>
                </Grid>

                {error && <h3>{error}</h3>}
                {loading && (
                  <Box display="flex" minHeight="300px" justifyContent="center" alignItems="center">
                    <CircularProgress />
                  </Box>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {data?.camera?.status?.rtsp && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <h1>RTSP Pushes</h1>
                <Grid container item spacing={2}>
                  {!data.camera.status.rtsp.online && (
                    <Grid item xs={12}>
                      <Alert severity="error">Not available because camera is offline.</Alert>
                    </Grid>
                  )}
                  {data.camera.status.rtsp.online && <RTSPPush camera={id} />}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container alignItems="center" spacing={2}>
                <Grid item flexGrow={1}>
                  <h1>Players</h1>
                </Grid>
                <Grid item>
                  {showPlayerEditLink && (
                    <Button
                      disabled={loading}
                      variant="contained"
                      color="secondary"
                      onClick={() => history.push(`/camera/${data?.camera?.id}/player/new/settings`)}
                    >
                      New
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                {data?.players?.map((player: Player) => {
                  return (
                    <Grid container item spacing={2} key={player.id}>
                      <Grid item xs>
                        {player.name}
                      </Grid>
                      <Grid item xs="auto">
                        {(player.settings?.image?.overlays?.hotlink?.label ||
                          player.settings?.video?.overlays?.hotlink?.label) && (
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                              setHotlinkPlayer(player);
                            }}
                          >
                            Hotlink
                          </Button>
                        )}
                      </Grid>
                      <Grid item xs="auto">
                        {showPlayerEditLink && (
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => history.push(`/camera/${data?.camera?.id}/player/${player.id}/settings`)}
                          >
                            Edit
                          </Button>
                        )}
                      </Grid>
                      <Grid item xs="auto">
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => history.push(`/camera/${data?.camera?.id}/player/${player.id}`)}
                        >
                          View
                        </Button>
                      </Grid>

                      {data?.camera?.client?.active !== false && data?.camera?.client?.demo && (
                        <Grid item xs="auto">
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => window.open(`//demo.hdrelay.com/demo/${player.id}`)}
                          >
                            Demo Link
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Dialog
        open={!!testResult}
        onClose={handleTestClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Test Result</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item container spacing={2}>
              <Grid item>
                <b>HTTP:</b>
              </Grid>
              <Grid item>
                {testResult?.http?.online === true && (
                  <Chip label="online" size="small" color="primary" sx={{ margin: '0 5px' }} />
                )}
                {testResult?.http?.online === false && (
                  <Chip label="offline" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                )}
                {testResult?.http?.replied === true && (
                  <Chip label="reply" size="small" color="primary" sx={{ margin: '0 5px' }} />
                )}
                {testResult?.http?.replied === false && (
                  <Chip label="not replying" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                )}
                {testResult?.http?.auth === true && (
                  <Chip label="authenticate" size="small" color="primary" sx={{ margin: '0 5px' }} />
                )}
                {testResult?.http?.auth === false && (
                  <Chip label="bad credentials" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                )}
                {testResult?.http?.timeout == true && (
                  <Chip label="timeout" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                )}
              </Grid>
            </Grid>
            {testResult?.rtsp && (
              <Grid item container spacing={2}>
                <Grid item>
                  <b>RTSP:</b>
                </Grid>
                <Grid item>
                  {testResult?.rtsp?.online === true && (
                    <Chip label="online" size="small" color="primary" sx={{ margin: '0 5px' }} />
                  )}
                  {testResult?.rtsp?.online === false && (
                    <Chip label="offline" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                  )}
                  {testResult?.rtsp?.connected === true && (
                    <Chip label="connect" size="small" color="primary" sx={{ margin: '0 5px' }} />
                  )}
                  {testResult?.rtsp?.connected === false && (
                    <Chip label="not connecting" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                  )}
                  {testResult?.rtsp?.requested === true && (
                    <Chip label="listening" size="small" color="primary" sx={{ margin: '0 5px' }} />
                  )}
                  {testResult?.rtsp?.requested === false && (
                    <Chip label="not listening" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                  )}
                  {testResult?.rtsp?.error == true && (
                    <Chip label="error" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                  )}
                  {testResult?.rtsp?.responseCode && (
                    <Chip
                      label={`Response: ${testResult.rtsp.responseCode}`}
                      size="small"
                      color={testResult.rtsp.responseCode == 200 ? 'primary' : 'secondary'}
                      sx={{ margin: '0 5px' }}
                    />
                  )}
                  {testResult?.rtsp?.timeout == true && (
                    <Chip label="timeout" size="small" color="secondary" sx={{ margin: '0 5px' }} />
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleTestClose} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <GoToCamera open={goToCamera} onClose={() => setGoToCamera(false)} id={id} />
      {hotlinkPlayer && (
        <HotLinkUpdate
          player={hotlinkPlayer}
          timezone={data.camera.timezone}
          camera={id}
          onClose={() => setHotlinkPlayer(null)}
        />
      )}
    </>
  );
}
